
import { useCallback, useState, useEffect, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { Order, Driver } from '../types';
import { mapCardapioToInternal } from '../services/cardapioWebMapper';
import { calculateDriverPerformance } from '../services/logistics/fleet/performanceTracker';
import { incrementDriverDailyDeliveries } from '../services/driverDailyProgress';
import {
    pollCardapioOrders,
    getCardapioOrderDetails,
    confirmCardapioOrder,
    startPreparationCardapioOrder,
    readyCardapioOrder,
    deliveredCardapioOrder,
    finalizeCardapioOrder
} from '../services/cardapioWebService';
import * as Sentry from "@sentry/react";
import { calculateBestInsertion } from '../services/logistics/routing/insertion';
import { logDeviation } from '../services/logistics/integrity/deviations';
import { requestOpenDeliveryDelivery } from '../services/openDeliveryLogisticsService';
import { shouldMaskGhostDispatchExternalStatus } from '../services/ghostDispatchPolicy';
import { resolveSyncedOrderStatus } from '../services/orderStatusSyncPolicy';


/**
 * v46.2: useSalActions - Strategic Decision Handlers
 * - Implementa a persistência de 'ignoredStrategies' para respeitar a decisão do usuário.
 * - Garante que estratégias rejeitadas não reapareçam para o mesmo pedido.
 */
export const useSalActions = (data: any, ui: any) => {
    const { orders, setOrders, setDrivers, config, providers, storeId, deleteOrder, deleteMultipleOrders } = data;
    const { setFocusedOrderId, setViewMode, toggleModal, setMapDisplayMode } = ui;
    const [isSyncing, setIsSyncing] = useState(false);

    // v58.6: Estado para rastrear estratégias ignoradas otimisticamente
    // Isso evita que o banner reapareça imediatamente após a recusa, antes da persistência no Supabase
    const [optimisticallyIgnoredStrategies, setOptimisticallyIgnoredStrategies] = useState<Set<string>>(new Set());

    // v58.7: TTL tracking para entries do estado otimista (evita memory leak)
    const entryTimestampsRef = useRef<Map<string, number>>(new Map());
    const TTL_MS = 5 * 60 * 1000; // 5 minutos TTL

    // v58.7: Helper puro - verifica se o pedido está em estado final (não ativo)
    const isOrderInactive = useCallback((order: Order | undefined): boolean => {
        if (!order) return true;
        const inactiveStatuses = new Set<Order['status']>(['delivered', 'canceled', 'archived', 'closed']);
        return inactiveStatuses.has(order.status);
    }, []);

    // v58.7: Helper puro - filtra entries expirados ou de pedidos inativos
    const filterStaleEntries = useCallback((
        entries: Set<string>,
        timestamps: Map<string, number>,
        currentOrders: Order[],
        now: number
    ): Set<string> => {
        const next = new Set<string>();
        const orderById = new Map(currentOrders.map(o => [o.id, o]));

        for (const key of entries) {
            const [orderId] = key.split(':');
            const order = orderById.get(orderId);
            const timestamp = timestamps.get(key) || now;
            const isExpired = now - timestamp > TTL_MS;
            const isInactive = isOrderInactive(order);

            if (!isExpired && !isInactive) {
                next.add(key);
            }
        }
        return next;
    }, [isOrderInactive]);

    const notifyExternalSystem = useCallback(async (order: Order, action: 'confirm' | 'ready' | 'dispatch' | 'deliver') => {
        if (order.source !== 'CARDAPIO_WEB' || !config.cardapioWeb?.active) return;
        const cwConfig = config.cardapioWeb;
        try {
            if (action === 'confirm') await confirmCardapioOrder(cwConfig, order.id);
            else if (action === 'deliver') {
                if (order.type === 'TAKEOUT') await finalizeCardapioOrder(cwConfig, order.id);
                else await deliveredCardapioOrder(cwConfig, order.id);
            }
            else if (action === 'ready') {
                if (order.status === 'scheduled_confirmed') {
                    await startPreparationCardapioOrder(cwConfig, order.id);
                }
                // v48.2: Permitir notificação de 'ready' para todos os tipos (incluindo Delivery)
                // Isso sincroniza o status 'Pronto' com o CardápioWeb/iFood
                await readyCardapioOrder(cwConfig, order.id);
            }
            else if (action === 'dispatch') {
                if (order.type === 'DELIVERY') {
                    if (order.status === 'scheduled_confirmed') {
                        await startPreparationCardapioOrder(cwConfig, order.id);
                    }
                    await readyCardapioOrder(cwConfig, order.id);
                }
            }
        } catch (error) {
            console.error("External Sync Error", error);
            Sentry.captureException(error, {
                tags: { feature: 'cardapio_web_sync' },
                extra: { action, orderId: order.id, source: order.source }
            });
        }
    }, [config.cardapioWeb]);

    const findProviderForOrder = useCallback((order: Order) => {
        const activeProviders = (providers || []).filter((p: any) => p.active);
        if (activeProviders.length === 0) return null;

        if (order.bestProvider) {
            const byName = activeProviders.find((p: any) => p.name === order.bestProvider);
            if (byName) return byName;
        }

        return activeProviders.find((p: any) => p.integrationType === 'open_delivery' && p.openDeliveryConfig?.active) || activeProviders[0];
    }, [providers]);

    const syncOpenDeliveryDispatch = useCallback(async (ordersToDispatch: Order[]) => {
        const externalOrders = ordersToDispatch.filter(order => {
            const provider = findProviderForOrder(order);
            return provider?.integrationType === 'open_delivery';
        });

        if (externalOrders.length === 0) {
            return;
        }

        const results = await Promise.all(externalOrders.map(async (order) => {
            const provider = findProviderForOrder(order);
            if (!provider) {
                return {
                    orderId: order.id,
                    providerName: 'open_delivery',
                    ok: false,
                    errorMessage: 'Sem parceiro configurado para envio externo'
                };
            }

            try {
                const response = await requestOpenDeliveryDelivery({
                    order,
                    provider,
                    config
                });

                return {
                    orderId: order.id,
                    providerName: provider.name,
                    ok: response.success,
                    deliveryId: response.deliveryId,
                    errorMessage: response.message
                };
            } catch (error) {
                return {
                    orderId: order.id,
                    providerName: provider.name,
                    ok: false,
                    errorMessage: error instanceof Error ? error.message : 'erro desconhecido'
                };
            }
        }));

        const failedMessages: string[] = [];
        const resultByOrderId = new Map<string, {
            at: number;
            providerName: string;
            ok: boolean;
            deliveryId?: string;
            message?: string;
        }>();

        results.forEach((result) => {
            const at = Date.now();
            const message = result.errorMessage || (result.ok ? 'Despacho confirmado' : 'falha ao enviar');
            resultByOrderId.set(result.orderId, {
                at,
                providerName: result.providerName,
                ok: result.ok,
                deliveryId: result.deliveryId,
                message
            });

            if (!result.ok) {
                failedMessages.push(`Pedido ${result.orderId}: ${message}`);
            }
        });

        if (resultByOrderId.size > 0) {
            setOrders((prev: Order[]) => prev.map(order => {
                const dispatchResult = resultByOrderId.get(order.id);
                if (!dispatchResult) return order;

                const attempts = [
                    ...(order.externalDispatchAttempts || []),
                    {
                        at: dispatchResult.at,
                        providerName: dispatchResult.providerName,
                        result: dispatchResult.ok ? 'success' as const : 'failed' as const,
                        message: dispatchResult.message
                    }
                ];

                if (dispatchResult.ok) {
                    return {
                        ...order,
                        externalCallConfirmed: true,
                        externalDeliveryId: dispatchResult.deliveryId || order.externalDeliveryId,
                        externalDispatchAttempts: attempts,
                        externalLastEvent: 'dispatch_confirmed',
                        externalLastEventAt: new Date(dispatchResult.at).toISOString(),
                        externalDispatchLastError: undefined,
                        externalDispatchLastErrorAt: undefined,
                        statusUpdatedAt: Date.now()
                    };
                }

                return {
                    ...order,
                    externalDispatchAttempts: attempts,
                    externalLastEvent: 'dispatch_failed',
                    externalLastEventAt: new Date(dispatchResult.at).toISOString(),
                    externalDispatchLastError: dispatchResult.message,
                    externalDispatchLastErrorAt: dispatchResult.at
                };
            }));
        }

        if (failedMessages.length > 0) {
            console.error('[OD Dispatch] Falhas de envio externo', failedMessages);
            toast.error(`Alguns envios externos falharam (${failedMessages.length}). Fallback local mantido.`);
        }
    }, [config, findProviderForOrder, setOrders]);

    // v58.6: Verificar se uma estratégia foi ignorada (estado persistido + estado otimista)
    const isStrategyIgnored = useCallback((orderId: string, strategy: string) => {
        const order = orders.find((o: Order) => o.id === orderId);
        if (!order) return false;
        const ignored = order.ignoredStrategies || [];
        const accepted = order.acceptedStrategies || [];
        const optimisticallyIgnored = optimisticallyIgnoredStrategies.has(`${orderId}:${strategy}`);
        return ignored.includes(strategy) || accepted.includes(strategy) || optimisticallyIgnored;
    }, [orders, optimisticallyIgnoredStrategies]);

    // --- NOVA LÓGICA: IGNORAR ESTRATÉGIA ---
    const handleIgnoreStrategy = useCallback((orderIds: string[], strategy: string, reason?: string) => {
        // v58.6: Atualização otimista - marca como ignorada imediatamente no estado local
        // Isso evita que o banner reapareça antes da persistência completa no Supabase
        // v58.7: Registra timestamp para TTL cleanup
        const now = Date.now();
        setOptimisticallyIgnoredStrategies(prev => {
            const next = new Set(prev);
            for (const orderId of orderIds) {
                const key = `${orderId}:${strategy}`;
                next.add(key);
                entryTimestampsRef.current.set(key, now);
            }
            return next;
        });

        setOrders((prev: Order[]) => prev.map(o => {
            if (orderIds.includes(o.id)) {
                const currentIgnored = o.ignoredStrategies || [];
                const currentAccepted = o.acceptedStrategies || [];
                if (!currentIgnored.includes(strategy)) {
                    const isBoomerangStrategy = strategy === 'boomerang' || strategy === 'bumerangue';
                    const isHoldStrategy = strategy === 'production_retention' || strategy === 'dispatch_hold';
                    return {
                        ...o,
                        ignoredStrategies: [...currentIgnored, strategy],
                        acceptedStrategies: currentAccepted.filter(s => isBoomerangStrategy ? (s !== 'boomerang' && s !== 'bumerangue') : s !== strategy),
                        isHeldForBatching: isHoldStrategy ? false : o.isHeldForBatching,
                        isBoomerangReserved: isBoomerangStrategy ? false : o.isBoomerangReserved,
                        boomerangPhase: isBoomerangStrategy ? undefined : o.boomerangPhase,
                        divergentStrategy: (isBoomerangStrategy && o.divergentStrategy === 'boomerang') || (isHoldStrategy && o.divergentStrategy === strategy)
                            ? undefined
                            : o.divergentStrategy,
                        divergentStrategyPhase: (isBoomerangStrategy || isHoldStrategy) ? undefined : o.divergentStrategyPhase,
                    };
                }
            }
            return o;
        }));

        // v50.6: Registro de Auditoria de Desvio
        if (reason) {
            logDeviation({
                type: 'strategy_ignore',
                reason,
                orderIds,
                strategyName: strategy,
                storeId
            }, data.userProfile);
        }

        let msg = "Entendido! Estratégia ignorada.";
        if (strategy === 'production_retention') msg = "Sofia: Entendido! Forçando o preparo desses pedidos agora.";
        else if (strategy === 'dispatch_hold') msg = "Sofia: Ok! Colocando esses pedidos na fila de despacho imediato.";
        else if (strategy === 'boomerang' || strategy === 'bumerangue') msg = "Sofia: Tudo bem! Despacho direto solicitado.";

        toast(msg, { icon: '🚀' });
    }, [setOrders, storeId, data.userProfile]);

    const handleConfirmStrategy = useCallback((orderIds: string[], strategy: string) => {
        setOrders((prev: Order[]) => prev.map(o => {
            if (!orderIds.includes(o.id)) return o;

            const currentAccepted = o.acceptedStrategies || [];
            const currentIgnored = o.ignoredStrategies || [];
            if (currentAccepted.includes(strategy) && !currentIgnored.includes(strategy)) return o;

            const baseUpdate = {
                acceptedStrategies: currentAccepted.includes(strategy) ? currentAccepted : [...currentAccepted, strategy],
                ignoredStrategies: currentIgnored.filter(s => s !== strategy),
                strategyConfirmedAt: Date.now()
            };

            if (strategy === 'production_retention' || strategy === 'dispatch_hold') {
                return {
                    ...o,
                    ...baseUpdate,
                    isHeldForBatching: true,
                    divergentStrategy: strategy,
                    divergentStrategyPhase: 'holding'
                };
            }

            if (strategy === 'boomerang' || strategy === 'bumerangue') {
                const phase: 'go' | 'return' = o.isReady || o.status === 'ready' ? 'go' : 'return';
                return {
                    ...o,
                    ...baseUpdate,
                    isBoomerangReserved: true,
                    boomerangPhase: o.boomerangPhase || phase,
                    divergentStrategy: 'boomerang',
                    divergentStrategyPhase: o.boomerangPhase || phase
                };
            }

            return {
                ...o,
                ...baseUpdate,
            };
        }));
    }, [setOrders]);

    const handleConfirmBoomerangStrategy = useCallback((goOrderIds: string[], returnOrderIds: string[]) => {
        const allOrderIds = new Set([...goOrderIds, ...returnOrderIds]);
        setOrders((prev: Order[]) => prev.map(o => {
            if (!allOrderIds.has(o.id)) return o;

            const currentAccepted = o.acceptedStrategies || [];
            const currentIgnored = o.ignoredStrategies || [];
            const phase: 'go' | 'return' = goOrderIds.includes(o.id) ? 'go' : 'return';

            return {
                ...o,
                acceptedStrategies: currentAccepted.includes('boomerang') ? currentAccepted : [...currentAccepted, 'boomerang'],
                ignoredStrategies: currentIgnored.filter(s => s !== 'boomerang' && s !== 'bumerangue'),
                isBoomerangReserved: true,
                boomerangPhase: phase,
                divergentStrategy: 'boomerang',
                divergentStrategyPhase: phase,
                strategyConfirmedAt: Date.now()
            };
        }));
    }, [setOrders]);

    // v58.6+v58.7: Limpar estado otimista quando pedidos forem atualizados (persistência confirmada)
    // Remove entries para pedidos inativos (delivered, canceled, archived, closed) e entries expirados (TTL)
    // Isso mantém o estado local limpo e evita memory leaks
    useEffect(() => {
        const now = Date.now();
        setOptimisticallyIgnoredStrategies(prev => {
            const next = filterStaleEntries(prev, entryTimestampsRef.current, orders, now);
            // Limpa timestamps órfãos (entries que foram removidos)
            for (const key of entryTimestampsRef.current.keys()) {
                if (!next.has(key)) {
                    entryTimestampsRef.current.delete(key);
                }
            }
            // Também remove entries persistidos (lógica original v58.6)
            let hasPersistedRemoved = false;
            for (const key of next) {
                const [orderId, strategy] = key.split(':');
                const order = orders.find((o: Order) => o.id === orderId);
                if (order) {
                    const ignored = order.ignoredStrategies || [];
                    const accepted = order.acceptedStrategies || [];
                    if (ignored.includes(strategy) || accepted.includes(strategy)) {
                        next.delete(key);
                        entryTimestampsRef.current.delete(key);
                        hasPersistedRemoved = true;
                    }
                }
            }
            return next.size !== prev.size || hasPersistedRemoved ? new Set(next) : prev;
        });
    }, [orders, filterStaleEntries]);

    // v58.7: Cleanup completo no unmount - previne vazamento de memória em ref
    useEffect(() => {
        return () => {
            entryTimestampsRef.current.clear();
            setOptimisticallyIgnoredStrategies(new Set());
        };
    }, []);

    const handleManualActiveSync = useCallback(async () => {
        if (isSyncing || !config.cardapioWeb?.active) return;
        setIsSyncing(true);
        const syncToastId = toast.loading("Sincronizando PDV...", { icon: '🔄' });
        Sentry.addBreadcrumb({ category: 'logistics', message: 'Manual sync started', level: 'info' });
        try {
            const pdvRaw = await pollCardapioOrders(config.cardapioWeb);
            const hubConfig = { lat: config.businessLat, lng: config.businessLng, googleMapsApiKey: config.googleMapsApiKey, hereMapsApiKey: config.hereMapsApiKey, storeId };
            const incomingFromPdv = (await Promise.all(
                pdvRaw.map(async (lite) => {
                    const fullDetails = await getCardapioOrderDetails(config.cardapioWeb, lite.id);
                    if (!fullDetails) return null;
                    return mapCardapioToInternal(fullDetails, hubConfig);
                })
            )).filter((mapped): mapped is Order => mapped !== null);

            const finalizedStatuses = new Set<Order['status']>(['delivered', 'archived', 'canceled']);
            setOrders((prev: Order[]) => {
                const nextState = [...prev];
                const indexById = new Map(nextState.map((order, idx) => [order.id, idx]));
                for (const mapped of incomingFromPdv) {
                    const idx = indexById.get(mapped.id);
                    if (idx === undefined) {
                        if (!finalizedStatuses.has(mapped.status)) {
                            nextState.push(mapped);
                            indexById.set(mapped.id, nextState.length - 1);
                        }
                    } else {
                        const existing = nextState[idx];
                        if (finalizedStatuses.has(existing.status)) continue;
                        const shouldMaskGhostDispatch = shouldMaskGhostDispatchExternalStatus(existing, mapped.status);
                        const incomingStatus = shouldMaskGhostDispatch ? existing.status : mapped.status;
                        const finalStatus = resolveSyncedOrderStatus(
                            { status: existing.status, driverId: existing.driverId },
                            incomingStatus
                        );

                        const effectiveIsReady = shouldMaskGhostDispatch
                            ? existing.isReady
                            : ((finalStatus === 'ready') ? true : (mapped.isReady ?? existing.isReady));

                        if (existing.status !== finalStatus || existing.isReady !== effectiveIsReady) {
                            nextState[idx] = { ...existing, status: finalStatus, isReady: effectiveIsReady, statusUpdatedAt: Date.now() };
                        }
                    }
                }
                return nextState;
            });
            toast.success(`Sincronizado`, { id: syncToastId });
        } catch (error) {
            Sentry.captureException(error, {
                tags: { feature: 'manual_active_sync' },
                extra: { storeId }
            });
            toast.error("Falha na sincronia.");
        } finally { setIsSyncing(false); }
    }, [config, isSyncing, setOrders, storeId]);

    // v60.2: Adicionado logging detalhado para diagnóstico de race condition no tablet
    const handleDispatchCluster = useCallback((ordersToDispatch: Order[], mode: 'own' | 'third', driverId?: string) => {
        const ids = new Set(ordersToDispatch.map(o => o.id));
        const existingMissionId = ordersToDispatch.find(o => !!o.smartClusterId)?.smartClusterId;
        const generatedMissionId = `route_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
        const missionId = existingMissionId || generatedMissionId;

        // v60.2: Log detalhado para diagnóstico
        console.log('[handleDispatchCluster] Iniciando dispatch:', {
            mode,
            driverId,
            orderIds: Array.from(ids),
            missionId,
            timestamp: Date.now(),
            userAgent: typeof navigator !== 'undefined' ? navigator.userAgent?.slice(0, 50) : 'unknown'
        });

        setOrders((prev: Order[]) => {
            const updatedOrders = prev.map(o => {
                if (!ids.has(o.id)) return o;

                const nextStatus: Order['status'] = mode === 'third' ? 'dispatched_3rd_party' : 'assigned';
                
                // Detecta transição de modo que requer limpeza de estado manual
                const wasOwn = o.manualAssignment === 'own' || o.status === 'assigned';
                const wasThird = o.manualAssignment === 'third' || o.status === 'dispatched_3rd_party';
                const isModeTransition = (wasOwn && mode === 'third') || (wasThird && mode === 'own');
                
                // Limpa estado manual em transições de modo para evitar inconsistências
                const shouldClearManualState = isModeTransition || mode === 'third';

                const updatedOrder = {
                    ...o,
                    status: nextStatus,
                    driverId: driverId || o.driverId,
                    smartClusterId: o.smartClusterId || missionId,
                    pickupConfirmed: mode === 'own' ? false : o.pickupConfirmed,
                    externalCallConfirmed: mode === 'third' ? false : o.externalCallConfirmed,
                    dispatchRequestedAt: Date.now(),
                    statusUpdatedAt: Date.now(),
                    // Limpa estado manual em transições para evitar vazamento
                    ...(shouldClearManualState && {
                        manualAssignment: undefined,
                        manualRouteGroup: undefined,
                        manualAssignmentReason: undefined,
                        isManuallyAdjusted: false
                    })
                };

                // v60.2: Log de cada pedido atualizado
                console.log('[handleDispatchCluster] Pedido atualizado:', {
                    orderId: o.id,
                    previousDriverId: o.driverId,
                    newDriverId: updatedOrder.driverId,
                    status: updatedOrder.status,
                    shouldClearManualState,
                    isModeTransition
                });

                return updatedOrder;
            });

            return updatedOrders;
        });

        Sentry.addBreadcrumb({
            category: 'logistics',
            message: `Cluster dispatched: ${mode}`,
            data: { count: ordersToDispatch.length, driverId },
            level: 'info'
        });
        toast.success(`${ordersToDispatch.length} pedidos despachados!`);

        if (mode === 'third') {
            void syncOpenDeliveryDispatch(ordersToDispatch);
        } else if (mode === 'own') {
            // v60.4: Lógica corrigida para Ghost Dispatch
            // - Quando DESLIGADO: despacha imediatamente
            // - Quando LIGADO: verifica se motoboy aceitou antes do trigger time
            const ghostDispatchEnabled = config.cardapioWeb?.enableGhostDispatch ?? false;
            
            // Filtra pedidos iFood elegíveis para despacho no CardápioWeb
            const cardapioWebOrders = ordersToDispatch.filter(o =>
                o.source === 'CARDAPIO_WEB' &&
                o.type === 'DELIVERY' &&
                o.salesChannel?.toLowerCase().includes('ifood')
            );
            
            if (cardapioWebOrders.length === 0) return;
            
            if (ghostDispatchEnabled) {
                // Ghost Dispatch LIGADO: verificar se algum pedido tem trigger pendente
                const ordersWithPendingTrigger = cardapioWebOrders.filter(o => 
                    o.ghostDispatchTriggerTime && 
                    !o.ghostDispatchSent &&
                    Date.now() < o.ghostDispatchTriggerTime
                );
                
                if (ordersWithPendingTrigger.length > 0) {
                    // Motoboy aceitou ANTES do trigger time - despachar imediatamente
                    // e cancelar o trigger para evitar duplo despacho
                    console.log('[handleDispatchCluster] Motoboy aceitou antes do trigger - despachando imediatamente:', ordersWithPendingTrigger.map(o => o.id));
                    
                    // Despacha imediatamente
                    void Promise.allSettled(ordersWithPendingTrigger.map(o => notifyExternalSystem(o, 'dispatch')));
                    
                    // Marca como despachado e limpa o trigger time no estado
                    setOrders((prev: Order[]) => prev.map(o => {
                        if (ordersWithPendingTrigger.some(pending => pending.id === o.id)) {
                            return {
                                ...o,
                                ghostDispatchSent: true,
                                ghostDispatchTriggerTime: undefined,
                                ghostDispatch: false // Não precisa mais do ghost dispatch
                            };
                        }
                        return o;
                    }));
                } else {
                    console.log('[handleDispatchCluster] Ghost Dispatch ativo - trigger será disparado automaticamente');
                }
            } else {
                // Ghost Dispatch DESLIGADO: despachar imediatamente
                console.log('[handleDispatchCluster] Ghost Dispatch desligado - despachando imediatamente');
                void Promise.allSettled(cardapioWebOrders.map(o => notifyExternalSystem(o, 'dispatch')));
            }
        }
    }, [setOrders, syncOpenDeliveryDispatch, notifyExternalSystem, config.cardapioWeb]);

    return {
        handleOrderParsed: (o: Order) => setOrders((p: Order[]) => [...p, o]),
        handleManualActiveSync,
        isSyncing,
        toggleModal,
        onOrderDelete: deleteOrder,
        handleDeleteMultipleOrders: deleteMultipleOrders,
        handleClearQueue: () => {
            const clearableStatuses = new Set<Order['status']>(['pending', 'confirmed', 'ready']);
            const ids: string[] = [];
            for (const order of orders as Order[]) {
                if (clearableStatuses.has(order.status)) ids.push(order.id);
            }
            deleteMultipleOrders(ids);
        },
        onOrderUpdate: (updated: Order) => setOrders((prev: Order[]) => prev.map(o => o.id === updated.id ? updated : o)),
        handleToggleReady: (id: string) => {
            setOrders((prev: Order[]) => {
                const target = prev.find(o => o.id === id);

                // v57.3: Notificar CardápioWeb apenas para pedidos de RETIRADA
                // Para Retirada: /ready muda para 'waiting_to_catch' (correto)
                // Para Delivery: /ready muda para 'released' (saiu p/ entrega) - NÃO QUEREMOS ISSO AQUI
                // Delivery só deve marcar 'released' no despacho real ou Ghost Dispatch
                if (target && !target.isReady && target.type === 'TAKEOUT') {
                    notifyExternalSystem(target, 'ready');
                }

                return prev.map(o => {
                    if (o.id === id) {
                        const newReadyState = !o.isReady;
                        return {
                            ...o,
                            isReady: newReadyState,
                            status: newReadyState ? ('ready' as Order['status']) : ('confirmed' as Order['status']),
                            statusUpdatedAt: Date.now()
                        };
                    }
                    return o;
                });
            });
        },
        handleConfirmOrder: (id: string) => {
            setOrders((prev: Order[]) => {
                const target = prev.find(o => o.id === id);
                if (target?.source === 'CARDAPIO_WEB' && target.status === 'waiting_confirmation') notifyExternalSystem(target, 'confirm');
                return prev.map(o => {
                    if (o.id !== id) return o;
                    const isScheduled = o.orderTiming === 'scheduled';
                    return { ...o, status: isScheduled ? 'scheduled_confirmed' : 'confirmed' };
                });
            });
        },
        handleAssignDriver: (oid: string, did: string) => {
            setOrders((prev: Order[]) => {
                const targetOrder = prev.find(o => o.id === oid);
                if (!targetOrder) return prev;

                const driverRoute = prev
                    .filter(o => o.driverId === did && ['assigned', 'dispatched', 'confirmed', 'ready'].includes(o.status))
                    .sort((a, b) => (a.routeSequence || 0) - (b.routeSequence || 0));

                const hub = { lat: config.businessLat, lng: config.businessLng };
                const cleanRoute = driverRoute.filter(o => o.id !== oid);
                const optimizedIndex = calculateBestInsertion(cleanRoute, targetOrder, hub);
                cleanRoute.splice(optimizedIndex, 0, targetOrder);
                const routeIndexById = new Map(cleanRoute.map((order, index) => [order.id, index]));

                return prev.map(o => {
                    const routeIndex = routeIndexById.get(o.id);
                    if (routeIndex !== undefined) {
                        return { ...o, status: 'assigned', driverId: did, routeSequence: routeIndex, statusUpdatedAt: Date.now() };
                    }
                    return o;
                });
            });
        },
        handleRealDelivery: (id: string, options?: { completedAt?: number; skipPerformanceIncrement?: boolean; skipExternalNotify?: boolean }) => {
            setOrders((prev: Order[]) => {
                const target = prev.find(o => o.id === id);
                if (target && !options?.skipExternalNotify) notifyExternalSystem(target, 'deliver');

                const completedAt = options?.completedAt || Date.now();
                const inferredServiceTimeMins = target?.arrivedAt && completedAt > target.arrivedAt
                    ? Number(((completedAt - target.arrivedAt) / 60000).toFixed(1))
                    : undefined;

                const updatedOrders = prev.map(o => o.id === id ? {
                    ...o,
                    status: 'delivered' as Order['status'],
                    deliveredAt: completedAt,
                    completedDriverId: o.driverId || o.completedDriverId,
                    serviceTimeMins: inferredServiceTimeMins ?? o.serviceTimeMins,
                    driverId: undefined,
                    isHeldForBatching: false,
                    isBoomerangReserved: false,
                    boomerangPhase: undefined,
                    divergentStrategy: undefined,
                    divergentStrategyPhase: undefined,
                    statusUpdatedAt: completedAt
                } : o);

                // v6.1: Recálculo de Performance em tempo real
                if (target?.driverId && !options?.skipPerformanceIncrement) {
                    const driver = data.drivers?.find((d: Driver) => d.id === target.driverId);
                    if (driver) {
                        const performance = calculateDriverPerformance(updatedOrders, driver);
                        void setDrivers((prevDrivers: Driver[]) => prevDrivers.map((current: Driver) => {
                            if (current.id !== driver.id) return current;
                            return {
                                ...incrementDriverDailyDeliveries(current, completedAt),
                                speedFactor: performance.speedFactor,
                                avgSpeedKmH: performance.avgSpeedKmH,
                                avgServiceTimeMinutes: performance.avgServiceTimeMinutes,
                            };
                        }));
                    }
                }

                return updatedOrders;
            });
        },
        handleDispatchCluster,
        handleApplyStrategy: (suggestion: any) => {
            if (!suggestion) return;
            suggestion.routes.forEach((r: any) => {
                const untamperedOrders = r.orders.filter((o: Order) => !o.isManuallyAdjusted && !o.manualAssignment);
                if (untamperedOrders.length > 0) {
                    handleDispatchCluster(untamperedOrders, r.type, r.assigneeId);
                }
            });
            Sentry.addBreadcrumb({ category: 'logistics', message: 'Sofia strategy applied', level: 'info' });
            toast.success("Plano Sofia aplicado com sucesso!");
        },
        handleAcceptWait: (ordersToWait: Order[]) => {
            // v57.1: Actually mark orders as held for batching so the strategy takes effect
            const ids = ordersToWait.map(o => o.id);
            setOrders((prev: Order[]) => prev.map(o => {
                if (ids.includes(o.id)) {
                    return { ...o, isHeldForBatching: true };
                }
                return o;
            }));
            toast.success("Sofia: Ótimo! Vou monitorar o tempo desses pedidos para garantir o SLA.", { icon: '❄️' });
        },
        handleApplySequential: () => {
            toast.success("Sofia: Bumerangue confirmado! O motorista já saberá qual a próxima perna ao retornar.", { icon: '🪃' });
        },
        handleConfirmBoomerangStrategy,
        handleConfirmStrategy,
        handleIgnoreStrategy,
        handleTrackOrder: (id: string) => { setFocusedOrderId(id); setViewMode('map'); setMapDisplayMode('recon'); },
        handleRealPickup: (did: string) => {
            const affectedOrders = orders.filter(o => o.driverId === did && (o.status === 'assigned' || (o.status === 'dispatched' && !o.pickupConfirmed)) && o.source === 'CARDAPIO_WEB' && o.type === 'DELIVERY');
            if (affectedOrders.length > 0) {
                void Promise.allSettled(affectedOrders.map(o => notifyExternalSystem(o, 'dispatch')));
            }

            setOrders((prev: Order[]) => {
                return prev.map(o => o.driverId === did && (o.status === 'assigned' || (o.status === 'dispatched' && !o.pickupConfirmed))
                    ? { ...o, pickupConfirmed: true, pickupConfirmedAt: Date.now(), status: 'dispatched' as Order['status'], statusUpdatedAt: Date.now() }
                    : o
                );
            });
            toast.success("Missão iniciada!");
        },
        handleArrived: (id: string) => {
            setOrders((prev: Order[]) => prev.map(o => o.id === id ? { ...o, arrivedAt: Date.now() } : o));
            toast.success("Chegada confirmada!");
        },
        handleSimulateWebhook: () => {
            toast("Simulando entrada de pedido...");
        },
        // v59.1: Corrigido vazamento de estado manual em recalls
        // Ao recolher um pedido, limpa todas as flags de atribuição manual
        handleRecallOrder: (id: string) => {
            const recallTimestamp = Date.now();
            setOrders((prev: Order[]) => prev.map(o => o.id === id ? {
                ...o,
                status: 'confirmed',
                driverId: undefined,
                pickupConfirmed: false,
                dispatchRequestedAt: undefined,
                // Limpa estado manual para evitar vazamento em recalls
                manualAssignment: undefined,
                manualRouteGroup: undefined,
                manualAssignmentReason: undefined,
                isManuallyAdjusted: false,
                statusUpdatedAt: recallTimestamp,
                // Marca que houve mudança local recente para proteção em refresh
                _localRecallAt: recallTimestamp
            } : o));
            toast("Pedido retornou para a triagem.");
        },
        handleDispatchThirdParty: (ordersToDispatch: Order[]) => handleDispatchCluster(ordersToDispatch, 'third'),
        handleManualAssignment: (oid: string, type: 'own' | 'third', reason?: string) => {
            setOrders((prev: Order[]) => prev.map(o => o.id === oid ? {
                ...o,
                manualAssignment: type,
                manualRouteGroup: o.manualRouteGroup || `${type}_route_1`,
                manualAssignmentReason: reason,
                isManuallyAdjusted: true,
                statusUpdatedAt: Date.now(),
            } : o));

            if (reason) {
                logDeviation({
                    type: 'manual_adjustment',
                    reason,
                    orderIds: [oid],
                    adjustmentType: type,
                    storeId
                }, data.userProfile);
            }

            toast.success("Atribuição manual concluída.");
        },
        handleBatchManualAssignment: (
            ownIds: string[],
            thirdIds: string[],
            reason?: string,
            ownRouteGroups?: Record<string, string>,
            thirdRouteGroups?: Record<string, string>,
            ownActiveRouteByOrder?: Record<string, string>
        ) => {
            setOrders((prev: Order[]) => {
                const now = Date.now();
                const adjustedIds = new Set([...ownIds, ...thirdIds]);
                const prevById = new Map(prev.map(order => [order.id, order]));

                const orderedOwn = ownIds
                    .map(id => prevById.get(id))
                    .filter((order): order is Order => !!order)
                    .map(order => ({
                        ...order,
                        manualAssignment: 'own' as const,
                        manualRouteGroup: ownRouteGroups?.[order.id] || 'own_route_1',
                        manualAssignmentReason: reason,
                        isManuallyAdjusted: true,
                        statusUpdatedAt: now
                    }));

                const orderedThird = thirdIds
                    .map(id => prevById.get(id))
                    .filter((order): order is Order => !!order)
                    .map(order => ({
                        ...order,
                        manualAssignment: 'third' as const,
                        manualRouteGroup: thirdRouteGroups?.[order.id] || 'third_route_1',
                        manualAssignmentReason: reason,
                        isManuallyAdjusted: true,
                        statusUpdatedAt: now
                    }));

                const ownById = new Map(orderedOwn.map(order => [order.id, order]));
                const routeOverrides = new Map<string, Order>();
                const ownByDriver = new Map<string, string[]>();

                ownIds.forEach(orderId => {
                    const driverId = ownActiveRouteByOrder?.[orderId];
                    if (!driverId || !ownById.has(orderId)) return;
                    const current = ownByDriver.get(driverId) || [];
                    current.push(orderId);
                    ownByDriver.set(driverId, current);
                });

                ownByDriver.forEach((orderIdsForDriver, driverId) => {
                    const currentRoute = prev
                        .filter(order =>
                            order.driverId === driverId &&
                            ['assigned', 'dispatched', 'confirmed', 'ready'].includes(order.status) &&
                            !adjustedIds.has(order.id)
                        )
                        .sort((a, b) => (a.routeSequence || 0) - (b.routeSequence || 0));

                    const routeAlreadyInProgress = currentRoute.some(order => order.status === 'dispatched' || !!order.pickupConfirmed);
                    const mutableRoute = [...currentRoute];
                    const hub = { lat: config.businessLat, lng: config.businessLng };

                    orderIdsForDriver.forEach(orderId => {
                        const ownOrder = ownById.get(orderId);
                        if (!ownOrder) return;
                        const insertionIndex = calculateBestInsertion(mutableRoute, ownOrder, hub);
                        mutableRoute.splice(insertionIndex, 0, ownOrder);
                    });

                    mutableRoute.forEach((routeOrder, routeIndex) => {
                        const isInsertedOrder = orderIdsForDriver.includes(routeOrder.id);

                        if (isInsertedOrder) {
                            routeOverrides.set(routeOrder.id, {
                                ...routeOrder,
                                driverId,
                                routeSequence: routeIndex,
                                status: routeAlreadyInProgress ? 'dispatched' : 'assigned',
                                pickupConfirmed: routeAlreadyInProgress ? true : false,
                                pickupConfirmedAt: routeAlreadyInProgress ? (routeOrder.pickupConfirmedAt || now) : undefined,
                                dispatchRequestedAt: routeOrder.dispatchRequestedAt || now,
                                statusUpdatedAt: now
                            });
                            return;
                        }

                        routeOverrides.set(routeOrder.id, {
                            ...routeOrder,
                            routeSequence: routeIndex,
                            statusUpdatedAt: now
                        });
                    });
                });

                const otherOrders = prev
                    .filter(order => !adjustedIds.has(order.id))
                    .map(order => routeOverrides.get(order.id) || order);

                const normalizedOwn = orderedOwn.map(order => routeOverrides.get(order.id) || order);

                return [...otherOrders, ...normalizedOwn, ...orderedThird];
            });

            if (reason) {
                logDeviation({
                    type: 'manual_adjustment',
                    reason,
                    orderIds: [...ownIds, ...thirdIds],
                    adjustmentType: ownIds.length > 0 ? 'own' : 'third',
                    storeId
                }, data.userProfile);
            }

            toast.success("Distribuição manual aplicada!");
        },
        onFinalizeDay: () => { if (window.confirm("Arquivar pedidos?")) { deleteMultipleOrders(orders.map(o => o.id)); ui.toggleModal('cashFlow', false); } },
        // v58.6: Expor função para verificar se uma estratégia foi ignorada (persistida ou otimisticamente)
        isStrategyIgnored
    };
};
